import { NaiveDate } from "@tento-chrono";
import { Tracing } from "@tento-core/tracing";
import { $$ } from "@tento-ui/fastdom";
import { LonaIcons, NumberIcon } from "@tento-ui/ui/icons";
import { NumberInput } from "@tento-ui/components/input/number-input";
import { NumberInputAccessory } from "@tento-ui/components/input/number-input-accessory";
import { CellHandle, CellTarget, SheetViewModel, type SheetRow } from "@tento-lona";
import { Actor, type Option } from "@tento-lona/sheets";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import {
  type BindCellContext,
  RenderPlugin,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import type { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import type { BaseAttributes } from "@tento-lona/sheets/platform";
import { HomeCardFactory, SheetRowNumberCard } from "@tento-lona/sheets-ui/home-card/index.ts";
import { COLUMN_NUMBER_SPEC, ColumnNumberRow } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import { storageCellDateForColumn } from "./cell-date.ts";
import { CellCommitBaseline } from "./cell-commit-baseline.ts";
import type { SheetBinder } from "../../sheet/sheet-index-renderer.ts";

/** The renderer plugin for a number row: one editable number per column. */
export class ColumnNumberRendererPlugin implements RenderPlugin.Renderer<
  NumberInput,
  SheetLegend.DefaultRow,
  ColumnNumberRow
> {
  static readonly spec: PerTypeSpec = COLUMN_NUMBER_SPEC;

  readonly role = "renderer";
  readonly id = "column.number";
  readonly rowClass: typeof ColumnNumberRow = ColumnNumberRow;
  readonly icon: HTMLTemplateElement = LonaIcons.$template(NumberIcon);

  #cellHandles = new Map<NumberInput, CellHandle>();
  /** Per-(rowId, column) actor for self-echo suppression — see
   *  `RenderPlugin.CellActors`. */
  #cellActors = new RenderPlugin.CellActors("column.number");

  constructor(
    readonly makeRowOptions: RowOptionsElement.Factory = RowOptionsElement
      .Factory.NOOP,
    readonly loadData: Option<
      (
        sheet: SheetViewModel.Surface<SheetBinder>,
        row: SheetRow.Joined.Handle,
        column: NaiveDate.Partial,
      ) => Promise<unknown>
    >,
  ) {}

  #disposeHandleForCell($cell: NumberInput): void {
    const existing = this.#cellHandles.get($cell);
    if (existing) {
      existing.dispose();
      this.#cellHandles.delete($cell);
    }
  }

  onCellRecycled($cell: NumberInput): void {
    this.#disposeHandleForCell($cell);
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return "Store numbers and track your finances, health metrics, and more.";
  }

  makeCell(): NumberInput {
    return NumberInput.make().assignStyles({
      "--padding-block": "10px",
      "--padding-inline": "10px",
      "--placeholder-left": "10px",
      "--placeholder-right": "10px",
      height: "100%",
      textAlign: "end",
      overflow: "hidden",
    });
  }

  async bindCell(
    $cell: NumberInput,
    { sheet: rowList, row, sheetRow, column, isCurrent }: BindCellContext<ColumnNumberRow>,
  ) {
    this.#disposeHandleForCell($cell);

    const cellDate = storageCellDateForColumn(sheetRow.timescaleSetting, column);
    const cellActor = this.#cellActors.get(sheetRow.id, column);
    const placeholder = isCurrent ? row.prompt : undefined;
    const format = row.format;

    $$.mutate(() =>
      $cell.toggleEditable(rowList.editable && row.editable),
    );

    // The latest COMMITTED cell value — the no-op guard compares against this,
    // not a stale bind-time closure. Self-echo (a fire from this cell's own
    // write) keeps it fresh without re-binding the input (which would clobber
    // in-flight typing), so a follow-up clear/edit isn't mis-compared. Each
    // write's refusal takes back only that write (by identity): the baseline
    // falls back to the newest write still standing or the confirmed value,
    // never to a refused predecessor. Another writer's change observed while
    // a write is pending is confirmed, but the pending write's later
    // acceptance still confirms it over that change (arrival order).
    const committed = new CellCommitBaseline<number>();

    // One commit seam for both keystroke + blur. `n === committed` skips a
    // genuine no-op (focus→blur unchanged, or clearing an already-empty cell);
    // `NaN` is dropped so partial input ("1.") doesn't write garbage. Clearing
    // (`n == null`) clears the cell and PERSISTS.
    const commit = (n: Option<number>, persist: "now" | "debounced") => {
      if (n != null && Number.isNaN(n)) return;
      if (n === committed.value) return;
      // The row's own cell write, through its single-cardinality facade (a
      // number row has one cell per column); `cellActor` tags the echo.
      // Keystrokes debounce the server write (durable); blur / Enter flushes
      // it immediately (checked, superseding a pending keystroke write). A
      // refusal changed nothing stored: the write stops counting toward the
      // baseline, so the same value committed again writes again, and the
      // refusal is reported — never left floating.
      const cells = sheetRow.writableSingle();
      if (cells == null) {
        Tracing.warn("ui")("column-number: the row is not a writable single-cardinality row", { rowId: sheetRow.id.toString() });
        return;
      }
      const target = CellTarget.date(cellDate);
      const write = committed.begin(n);
      const settled = persist === "debounced"
        ? (n == null ? cells.clearDurable(target, cellActor) : cells.writeDurable(target, n, cellActor)).settled
        : n == null
        ? cells.clear(target, cellActor)
        : cells.write(target, n, cellActor);
      settled.then(
        () => committed.accepted(write),
        (err: unknown) => {
          committed.refused(write);
          Tracing.warn("net")("column-number/write failed", { rowId: sheetRow.id.toString(), error: String(err) });
        },
      );
    };

    const bindWithData = (data: Option<number>, loading: boolean = false) => {
      committed.bind(data);
      $cell.setCallbacks({
        onChange: (n) => commit(n ?? null, "debounced"),
        onEditingFinished: (n) => commit(n ?? null, "now"),
      });
      $$.mutate(() => {
        $cell.bind(data, {
          format,
          placeholder,
          loading,
        });
      });
    };

    // A recycled cell still shows, and writes to, the column it was bound to
    // before. Point it at this column, empty, before the handle answers: a
    // handle that never fires for this day (a formula over a missing input
    // has no value) leaves the cell blank, not holding another day's value.
    bindWithData(null);

    const h = sheetRow.single()?.cell(cellDate);
    if (h == null) return;
    this.#cellHandles.set($cell, h);
    h.onLoading(() => bindWithData(null, true));
    h.onData((cell, change) => {
      const value = typeof cell?.value === "number" ? cell.value : null;
      // Self-echo: this fire came from this cell's own write. Keep `committed`
      // fresh for the guard, but don't re-bind — that would clobber in-flight
      // typing in the input.
      if (change?.actor === cellActor) {
        committed.echo(value);
        return;
      }
      bindWithData(value);
    });
  }

  onResize(_$cell: NumberInput, _context: BindCellContext<ColumnNumberRow>): void {}

  legendFactory: Option<SheetLegendClass<SheetLegend.DefaultRow>> = null;

  onBindRow(_sheet: SheetDelegate, _row: SheetRow.Joined.Handle): void {}
  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}

  homeCardFactory: HomeCardFactory<SheetRowNumberCard> = {
    make: (ctx) => ({
      $cardContent: SheetRowNumberCard.makeWith({
        value: null,
        onUpdate: (value) => writeNumberDurable(ctx.row, ctx.datePartial, value ?? null),
      }),
      $listContent: null,
      $listAccessory: NumberInputAccessory.makeWith({ value: 0 }),
    }),

    bind: (content, ctx) => {
      const raw = ctx.row.single()?.peek(ctx.datePartial)?.value ?? null;
      content.$cardContent.bind({
        value: typeof raw === "number" ? raw : null,
        onUpdate: (value) => writeNumberDurable(ctx.row, ctx.datePartial, value ?? null),
      });
    },
  };
}

/**
 * Write a number row's cell for `day` durably, through the row's
 * single-cardinality facade (a number row has one cell per column); the
 * refusal is reported, never left floating.
 */
function writeNumberDurable(row: SheetRow.Joined.Handle, day: NaiveDate.Partial, value: Option<number>): void {
  const cells = row.writableSingle();
  if (cells == null) {
    Tracing.warn("ui")("column-number: the row is not a writable single-cardinality row", { rowId: row.id.toString() });
    return;
  }
  cells.writeDurable(CellTarget.date(day), value, Actor.EXTERNAL).settled.catch((err: unknown) =>
    Tracing.warn("net")("column-number/card write failed", { rowId: row.id.toString(), error: String(err) })
  );
}

/** Types that go with `ColumnNumberRendererPlugin`: `Attributes`. */
export namespace ColumnNumberRendererPlugin {
  export interface Attributes extends BaseAttributes {
    type?: ColumnNumberRow.Format;
  }
}
